// Ranking engine: turns knockout match results into ranking points.
//
// The points table and the "consolation entrants score consolation points
// only" rule were inferred from the existing Rankings.json and reproduce 98.5%
// of its rows exactly from darts-results.json (the rest are one-off manual
// adjustments, which are carried through as per-result overrides).

export var POINTS = {
  "Grade A": {
    Main: { "Round of 64": 100, "Round of 32": 200, "Round of 16": 300, "Quarter Finals": 400, "Semi Finals": 560, "Final": 760 },
    Consolation: { "Consolation Round of 32": 100, "Consolation Round of 16": 132, "Consolation Quarter Finals": 164, "Consolation Semi Finals": 200, "Consolation Final": 252 },
  },
  "Grade B": {
    Main: { "Round of 32": 100, "Round of 16": 152, "Quarter Finals": 200, "Semi Finals": 280, "Final": 380 },
    Consolation: { "Consolation Round of 16": 52, "Consolation Quarter Finals": 84, "Consolation Semi Finals": 92, "Consolation Final": 124 },
  },
  "Grade C": {
    Main: { "Round of 16": 88, "Quarter Finals": 120, "Semi Finals": 180, "Final": 240 },
    Consolation: { "Consolation Quarter Finals": 52, "Consolation Semi Finals": 64, "Consolation Final": 80 },
  },
};

export var WIN_POINTS = {
  "Grade A": { Main: 1000, Consolation: 300 },
  "Grade B": { Main: 500, Consolation: 152 },
  "Grade C": { Main: 300, Consolation: 88 },
};

export var GRADES = ["Grade A", "Grade B", "Grade C"];

export var STAGES = [
  "Round Robin",
  "Round of 64", "Round of 32", "Round of 16", "Quarter Finals", "Semi Finals", "Final",
  "Consolation Round of 32", "Consolation Round of 16", "Consolation Quarter Finals", "Consolation Semi Finals", "Consolation Final",
];

export var WINDOW_MONTHS = 12;

// Collapses stray/double whitespace so "Ruddock, Graham " and "Ruddock, Graham"
// are one player.
export function cleanName(s) {
  return (s || "").replace(/\s+/g, " ").trim();
}

// Thursday club nights are results/averages only — they never score ranking points.
export function isCountingTournament(name) {
  return !/thurs/i.test(name || "");
}

export function drawOf(stage) {
  return cleanName(stage).indexOf("Consolation") === 0 ? "Consolation" : "Main";
}

function isKnockoutStage(stage) {
  var s = cleanName(stage);
  return s !== "" && s !== "Round Robin" && s !== "Consolation Other";
}

export function overrideKey(player, grade, draw) {
  return cleanName(player) + "|" + grade + "|" + draw;
}

// Ranking rows for ONE tournament's matches.
// overrides: { "player|grade|draw": points } — manual point adjustments.
// Returns { rows, warnings }; rows are { player, grade, draw, stage, outcome, points, overridden }.
export function deriveResults(matches, overrides) {
  overrides = overrides || {};
  var warnings = [];
  var warned = {};
  function warn(key, text) {
    if (warned[key]) return;
    warned[key] = true;
    warnings.push(text);
  }

  var knockout = matches.filter(function(m) { return isKnockoutStage(m.stage); });

  // Anyone who plays a consolation match scores consolation points only.
  var inConsolation = {};
  knockout.forEach(function(m) {
    if (drawOf(m.stage) !== "Consolation") return;
    inConsolation[m.grade + "|" + cleanName(m.player1)] = true;
    inConsolation[m.grade + "|" + cleanName(m.player2)] = true;
  });

  var out = {};
  function put(player, grade, draw, stage, outcome, points) {
    var key = overrideKey(player, grade, draw);
    var overridden = typeof overrides[key] === "number";
    var row = { player: cleanName(player), grade: grade, draw: draw, stage: stage, outcome: outcome, points: overridden ? overrides[key] : points, overridden: overridden };
    if (!out[key] || row.points > out[key].points) out[key] = row;
  }

  knockout.forEach(function(m) {
    var stage = cleanName(m.stage);
    var draw = drawOf(stage);
    var grade = m.grade;
    var p1 = cleanName(m.player1);
    var p2 = cleanName(m.player2);
    var s1 = Number(m.score1);
    var s2 = Number(m.score2);
    if (!p1 || !p2) return;
    if (!isFinite(s1) || !isFinite(s2) || s1 === s2) {
      warn("score|" + p1 + "|" + p2 + "|" + stage, "Match " + p1 + " v " + p2 + " (" + stage + ") has no clear winner.");
      return;
    }
    var winner = s1 > s2 ? p1 : p2;
    var loser = s1 > s2 ? p2 : p1;

    var table = POINTS[grade] && POINTS[grade][draw];
    if (!table || table[stage] == null) {
      warn("stage|" + grade + "|" + stage, "No points defined for " + (grade || "no grade") + " – " + stage + ".");
      return;
    }

    var loserEnteredConsolation = draw === "Main" && inConsolation[grade + "|" + loser];
    if (!loserEnteredConsolation) put(loser, grade, draw, stage, "L", table[stage]);

    if (stage === "Final" || stage === "Consolation Final") {
      put(winner, grade, draw, stage, "W", WIN_POINTS[grade][draw]);
    }
  });

  return { rows: Object.keys(out).map(function(k) { return out[k]; }), warnings: warnings };
}

export function groupByTournament(matches) {
  var groups = {};
  matches.forEach(function(m) {
    var key = m.tournament + "|" + m.date;
    if (!groups[key]) groups[key] = { tournament: m.tournament, date: m.date, matches: [] };
    groups[key].matches.push(m);
  });
  return Object.keys(groups).map(function(k) { return groups[k]; });
}

// --- date helpers (ISO yyyy-mm-dd strings compare correctly as text) ---------

function pad(n, w) {
  var s = String(n);
  while (s.length < w) s = "0" + s;
  return s;
}

export function toIso(date) {
  return date.getUTCFullYear() + "-" + pad(date.getUTCMonth() + 1, 2) + "-" + pad(date.getUTCDate(), 2);
}

export function shiftMonths(iso, delta) {
  var y = parseInt(iso.slice(0, 4), 10);
  var m = parseInt(iso.slice(5, 7), 10) - 1;
  var d = parseInt(iso.slice(8, 10), 10);
  var t = y * 12 + m + delta;
  var ny = Math.floor(t / 12);
  var nm = ((t % 12) + 12) % 12;
  var daysInMonth = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  return pad(ny, 4) + "-" + pad(nm + 1, 2) + "-" + pad(Math.min(d, daysInMonth), 2);
}

export function shiftDays(iso, delta) {
  var dt = new Date(iso + "T00:00:00Z");
  dt.setUTCDate(dt.getUTCDate() + delta);
  return toIso(dt);
}

// --- rankings ---------------------------------------------------------------

function totalsByPlayer(rows, asOfIso, months) {
  var cutoff = shiftMonths(asOfIso, -months);
  var byPlayer = {};
  rows.forEach(function(r) {
    if (r.date < cutoff || r.date > asOfIso) return;
    if (!byPlayer[r.player]) byPlayer[r.player] = { player: r.player, total_points: 0, results: [] };
    byPlayer[r.player].total_points += r.points;
    byPlayer[r.player].results.push(r);
  });
  var list = Object.keys(byPlayer).map(function(k) { return byPlayer[k]; });
  list.sort(function(a, b) {
    return b.total_points - a.total_points || a.player.localeCompare(b.player);
  });
  return list;
}

// Every individual ranking result, independent of any date window — the single
// list that rankings for ANY "as of" date are cut from.
//
// staticRankings: Rankings.json (historical tournaments, verbatim — keeps the
//                 odd one-off point adjustments).
// staticMatches:  darts-results.json — tournaments with match data but no row in
//                 Rankings.json (e.g. ones already too old when it was built)
//                 are derived from their matches.
// tournaments:    Firestore tournamentResults docs
//                 { name, date, countsForRankings, matches, pointOverrides }.
// A Firestore tournament with the same name+date replaces its static counterpart.
export function buildResultRows(opts) {
  var staticRankings = opts.staticRankings || [];
  var staticMatches = opts.staticMatches || [];
  var tournaments = opts.tournaments || [];

  var replaced = {};
  tournaments.forEach(function(t) { replaced[t.name + "|" + t.date] = true; });

  var rows = [];
  var inRankingsFile = {};
  staticRankings.forEach(function(p) {
    (p.results || []).forEach(function(r) {
      inRankingsFile[r.tournament + "|" + r.date] = true;
      if (replaced[r.tournament + "|" + r.date]) return;
      // Only a final can be "won". A win at any other stage is an artefact of
      // the same person being recorded under two spellings (their win under
      // one name, their next match under another) — once names are merged it
      // would double count them, so it's dropped.
      if (r.outcome === "W" && r.stage !== "Final" && r.stage !== "Consolation Final") return;
      rows.push({
        player: cleanName(p.player), tournament: r.tournament, date: r.date, grade: r.grade,
        draw: r.draw, stage: r.stage, outcome: r.outcome, points: r.points,
      });
    });
  });

  groupByTournament(staticMatches).forEach(function(g) {
    var key = g.tournament + "|" + g.date;
    if (inRankingsFile[key] || replaced[key] || !isCountingTournament(g.tournament)) return;
    deriveResults(g.matches).rows.forEach(function(r) {
      rows.push({
        player: r.player, tournament: g.tournament, date: g.date, grade: r.grade,
        draw: r.draw, stage: r.stage, outcome: r.outcome, points: r.points,
      });
    });
  });

  tournaments.forEach(function(t) {
    if (!t.countsForRankings) return;
    var derived = deriveResults(t.matches || [], t.pointOverrides || {});
    derived.rows.forEach(function(r) {
      rows.push({
        player: r.player, tournament: t.name, date: t.date, grade: r.grade,
        draw: r.draw, stage: r.stage, outcome: r.outcome, points: r.points,
      });
    });
  });

  return rows;
}

export function earliestResultDate(rows) {
  var min = null;
  rows.forEach(function(r) { if (!min || r.date < min) min = r.date; });
  return min;
}

export function buildRankings(opts) {
  return rankingsFromRows(buildResultRows(opts), opts.asOf, opts.windowMonths);
}

// Rankings as they stood on `asOf` (default: now): the trailing 12 months of
// results. Result shape matches Rankings.json:
// { player, total_points, prev_rank, prev_points, results[] }.
export function rankingsFromRows(rows, asOf, windowMonths) {
  var months = windowMonths || WINDOW_MONTHS;
  var asOfIso = typeof asOf === "string" ? asOf : toIso(asOf || new Date());

  var current = totalsByPlayer(rows, asOfIso, months);

  // Movement is measured against the rankings as they stood the day before the
  // most recent counting tournament.
  var latest = null;
  rows.forEach(function(r) { if (r.date <= asOfIso && (!latest || r.date > latest)) latest = r.date; });
  var prevPoints = {};
  var prevRank = {};
  if (latest) {
    totalsByPlayer(rows, shiftDays(latest, -1), months).forEach(function(p, i) {
      prevPoints[p.player] = p.total_points;
      prevRank[p.player] = i + 1;
    });
  }

  return current.map(function(p) {
    p.results.sort(function(a, b) { return b.date.localeCompare(a.date); });
    return {
      player: p.player,
      total_points: p.total_points,
      prev_rank: prevRank[p.player] != null ? prevRank[p.player] : null,
      prev_points: prevPoints[p.player] != null ? prevPoints[p.player] : null,
      results: p.results,
    };
  });
}

// Static matches + Firestore tournaments as a single match list in the shape
// of darts-results.json. Firestore replaces a static tournament of the same name+date.
export function mergeMatches(staticMatches, tournaments) {
  var replaced = {};
  tournaments.forEach(function(t) { replaced[t.name + "|" + t.date] = true; });

  var out = [];
  staticMatches.forEach(function(m) {
    if (replaced[m.tournament + "|" + m.date]) return;
    out.push(Object.assign({}, m, {
      player1: cleanName(m.player1), player2: cleanName(m.player2), stage: cleanName(m.stage),
    }));
  });

  tournaments.forEach(function(t) {
    (t.matches || []).forEach(function(m, i) {
      out.push({
        id: "fs-" + t.id + "-" + i,
        date: t.date,
        tournament: t.name,
        stage: cleanName(m.stage),
        group: m.group || null,
        player1: cleanName(m.player1),
        player2: cleanName(m.player2),
        score1: m.score1,
        score2: m.score2,
        winner_avg: m.winner_avg != null ? m.winner_avg : null,
        loser_avg: m.loser_avg != null ? m.loser_avg : null,
        grade: m.grade || null,
      });
    });
  });
  return out;
}

export function playerNamesFrom(matches, rankings) {
  var seen = {};
  matches.forEach(function(m) { seen[m.player1] = 1; seen[m.player2] = 1; });
  rankings.forEach(function(r) { seen[r.player] = 1; });
  return Object.keys(seen).filter(Boolean).sort();
}

// ---- one spelling per player -------------------------------------------------
//
// "hunt, Daniel", "HUNT, DANIEL" and "Hunt, Daniel" are the same person. Every
// name is mapped onto a single canonical spelling, so a typo in the case can
// never split a player into two profiles / two ranking rows.

function startsEveryWordUpperCase(s) {
  return s.split(" ").every(function(w) { return w.charAt(0) === w.charAt(0).toUpperCase(); });
}

function titleCaseName(s) {
  return s.replace(/(^|[\s'’-])([a-z])/g, function(_, pre, ch) { return pre + ch.toUpperCase(); });
}

// names: array of strings (repeats count — the most used well-formed spelling wins).
export function buildNameCanon(names) {
  var groups = {};
  (names || []).forEach(function(n) {
    var c = cleanName(typeof n === "string" ? n : n && n.name);
    if (!c) return;
    var key = c.toLowerCase();
    if (!groups[key]) groups[key] = {};
    groups[key][c] = (groups[key][c] || 0) + 1;
  });

  var canonByKey = {};
  Object.keys(groups).forEach(function(key) {
    var counts = groups[key];
    var variants = Object.keys(counts).sort(function(a, b) {
      var fa = startsEveryWordUpperCase(a) ? 1 : 0;
      var fb = startsEveryWordUpperCase(b) ? 1 : 0;
      if (fa !== fb) return fb - fa;                       // properly capitalised first
      if (counts[a] !== counts[b]) return counts[b] - counts[a]; // then the most used
      return a.localeCompare(b);
    });
    canonByKey[key] = variants[0];
  });

  return function canon(name) {
    var c = cleanName(name);
    if (!c) return c;
    return canonByKey[c.toLowerCase()] || titleCaseName(c);
  };
}

export function canonicaliseMatchNames(matches, canon) {
  return matches.map(function(m) {
    return Object.assign({}, m, { player1: canon(m.player1), player2: canon(m.player2) });
  });
}

// A Firestore result set with every name (and point-override key) canonicalised.
export function canonicaliseTournament(t, canon) {
  var overrides = {};
  Object.keys(t.pointOverrides || {}).forEach(function(k) {
    var parts = k.split("|");
    parts[0] = canon(parts[0]);
    overrides[parts.join("|")] = t.pointOverrides[k];
  });
  return Object.assign({}, t, {
    matches: canonicaliseMatchNames(t.matches || [], canon),
    pointOverrides: overrides,
  });
}

// Everything the site reads, with one spelling per player throughout.
export function canonicaliseAll(opts) {
  var staticMatches = opts.staticMatches || [];
  var staticRankings = opts.staticRankings || [];
  var tournaments = opts.tournaments || [];

  var names = [];
  staticMatches.forEach(function(m) { names.push(m.player1, m.player2); });
  staticRankings.forEach(function(p) { names.push(p.player); });
  tournaments.forEach(function(t) {
    (t.matches || []).forEach(function(m) { names.push(m.player1, m.player2); });
  });
  var canon = buildNameCanon(names);

  return {
    canon: canon,
    staticMatches: canonicaliseMatchNames(staticMatches, canon),
    staticRankings: staticRankings.map(function(p) { return Object.assign({}, p, { player: canon(p.player) }); }),
    tournaments: tournaments.map(function(t) { return canonicaliseTournament(t, canon); }),
  };
}
